'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Typography } from '../../../components';
import { useTranslations } from '../../../contexts/translation.context';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { FiltersSection, TransactionsTable } from './components';
import {
  TransactionsFilters,
  RevshareTransaction,
  AvailableModel,
  AvailableCampaign,
} from './types';
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_DELAY } from './constants';
import partnerRevshareService, {
  TransactionType,
} from '../../../api/partner/partnerRevshareService';

const RevshareTransactions: React.FC = () => {
  const { t } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(
    () => [
      { label: t('sidebar.dashboard') || 'Dashboard', href: '/partner/dashboard' },
      { label: 'Revshare', href: '/partner/revshare/offers' },
      { label: t('partner.revshare.transactions.title') || 'Transactions', isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [transactions, setTransactions] = useState<RevshareTransaction[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [availableCampaigns, setAvailableCampaigns] = useState<AvailableCampaign[]>([]);

  // Totals from API
  const [apiTotalAmount, setApiTotalAmount] = useState(0);
  const [apiTotalFee, setApiTotalFee] = useState(0);

  // Filters
  const [filters, setFilters] = useState<TransactionsFilters>(DEFAULT_FILTERS);
  const [localSearch, setLocalSearch] = useState('');

  // Calculate totals from current page data (fallback)
  const totalAmount = useMemo(() => {
    if (apiTotalAmount > 0) return apiTotalAmount;
    return transactions.reduce((sum, tr) => sum + (parseFloat(tr.amount) || 0), 0);
  }, [transactions, apiTotalAmount]);

  const totalIncome = useMemo(() => {
    const incomeAmount =
      apiTotalFee > 0
        ? apiTotalFee
        : transactions.reduce((sum, tr) => sum + (parseFloat(tr.fee) || 0), 0);

    // Calculate percentage from fee/amount
    const percent = totalAmount > 0 ? Math.round((incomeAmount / totalAmount) * 100) : 0;

    return { amount: incomeAmount, percent };
  }, [transactions, apiTotalFee, totalAmount]);

  // Check if filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.account !== '' ||
      filters.campaign !== '' ||
      filters.period !== '' ||
      filters.type !== '' ||
      filters.datePeriod !== '' ||
      (filters.dateRange !== 'all' && filters.dateRange !== '') ||
      filters.customStartDate !== null ||
      filters.customEndDate !== null ||
      filters.status !== ''
    );
  }, [filters]);

  // Load available models from API
  const fetchAvailableModels = useCallback(async (search: string = '') => {
    setModelsLoading(true);
    try {
      const response = await partnerRevshareService.getCompaniesModels({
        page: 1,
        page_size: 50,
        search: search || undefined,
      });
      if (response.objects) {
        setAvailableModels(
          response.objects.map((acc) => ({
            id: acc._id,
            name: `${String(acc.of_user_id).slice(-4)}`,
            username: acc.of_user_name || '',
            avatar: acc.avatar || '',
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  // Initial load of models - only once on mount
  useEffect(() => {
    fetchAvailableModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate date range based on period filter or dateRange filter
  const getDateRange = useCallback(() => {
    const today = new Date();
    let startDate: Date | null = null;

    // First check custom date range from DateFilterSelect
    if (filters.dateRange === 'custom' && filters.customStartDate && filters.customEndDate) {
      return {
        start_date: filters.customStartDate,
        end_date: filters.customEndDate,
      };
    }

    // Handle preset periods from DateFilterSelect
    switch (filters.dateRange) {
      case 'today':
        return {
          start_date: today.toISOString().split('T')[0],
          end_date: today.toISOString().split('T')[0],
        };
      case 'week':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        break;
      case 'month':
        startDate = new Date(today);
        startDate.setMonth(startDate.getMonth() - 1);
        break;
      case 'quarter':
        startDate = new Date(today);
        startDate.setMonth(startDate.getMonth() - 3);
        break;
      case 'halfyear':
        startDate = new Date(today);
        startDate.setMonth(startDate.getMonth() - 6);
        break;
      case 'year':
        startDate = new Date(today);
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
      case 'all':
      case '':
        return { start_date: undefined, end_date: undefined };
    }

    // Also check period filter (7days, 14days, 30days)
    if (!startDate) {
      switch (filters.period) {
        case '7days':
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '14days':
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 14);
          break;
        case '30days':
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 30);
          break;
        default:
          return { start_date: undefined, end_date: undefined };
      }
    }

    if (startDate) {
      return {
        start_date: startDate.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0],
      };
    }

    return { start_date: undefined, end_date: undefined };
  }, [filters.dateRange, filters.customStartDate, filters.customEndDate, filters.period]);

  // Ref to track current request ID (to handle StrictMode double-invocation)
  const requestIdRef = useRef(0);
  // Ref to track if campaigns have been loaded (to avoid infinite loop)
  const campaignsLoadedRef = useRef(false);
  // Ref to skip debounce on initial mount
  const isInitialMount = useRef(true);

  // Fetch transactions from API - directly in useEffect to avoid double render
  useEffect(() => {
    // Increment request ID to invalidate any in-flight requests
    const currentRequestId = ++requestIdRef.current;

    setIsLoading(true);

    const fetchData = async () => {
      try {
        const dateRange = getDateRange();

        const params: any = {
          page: currentPage,
          page_size: pageSize,
          ...dateRange,
        };

        // Search by id
        if (filters.search) {
          params.id = filters.search;
        }

        // Model/Account filter
        if (filters.account) {
          params.model_id = filters.account;
        }

        // Campaign type filter (cpl/refshare)
        if (filters.campaign === 'cpl' || filters.campaign === 'refshare') {
          params.campaign_type = filters.campaign;
        } else if (filters.campaign) {
          params.company_id = filters.campaign;
        }

        // Type filter
        if (filters.type) {
          params.type = filters.type as TransactionType;
        }

        // Status filter
        if (filters.status) {
          params.status = filters.status;
        }

        // Fetch transactions
        const response = await partnerRevshareService.getTransactions(params);

        // Only update state if this is still the current request
        if (requestIdRef.current !== currentRequestId) {
          return;
        }

        setTransactions(response.objects);
        setTotalCount(response.count);

        // Set totals from API response
        if (response.total_amount) {
          setApiTotalAmount(parseFloat(response.total_amount) || 0);
        }
        if (response.total_fee) {
          setApiTotalFee(parseFloat(response.total_fee) || 0);
        }

        // Extract unique campaigns from response for filters (only once)
        if (!campaignsLoadedRef.current) {
          const campaignsMap = new Map<string, AvailableCampaign>();

          response.objects.forEach((tr) => {
            if (tr.company) {
              const companyId = tr.company.id || tr.company._id || '';
              campaignsMap.set(companyId, {
                id: companyId,
                name: `Campaign ${tr.company.of_id}`,
              });
            }
          });

          if (campaignsMap.size > 0) {
            setAvailableCampaigns(Array.from(campaignsMap.values()));
            campaignsLoadedRef.current = true;
          }
        }
      } catch (error: any) {
        // Only handle error if this is still the current request
        if (requestIdRef.current !== currentRequestId) {
          return;
        }
        console.error('Error fetching transactions:', error);
        setTransactions([]);
        setTotalCount(0);
      } finally {
        // Only update loading state if this is still the current request
        if (requestIdRef.current === currentRequestId) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [currentPage, pageSize, filters, getDateRange]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Debounce search - only update if value actually changed, skip on initial mount
  useEffect(() => {
    // Skip debounce on initial mount to prevent double render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search !== localSearch) {
          return { ...prev, search: localSearch };
        }
        return prev;
      });
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timeoutId);
  }, [localSearch]);

  const handleFilterChange = useCallback((key: keyof TransactionsFilters, value: string | null) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleModelSearchChange = useCallback(
    (search: string) => {
      fetchAvailableModels(search);
    },
    [fetchAvailableModels]
  );

  const handleLoadChart = useCallback(() => {
    // TODO: Implement chart loading
    console.log('Load chart');
  }, []);

  return (
    <div className="w-full px-4 py-6 md:p-6">
      {/* Header */}
      <div className="mb-6 ">
        <Typography variant="h4" className="mb-2">
          {t('partner.revshare.transactions.title')}
        </Typography>
      </div>

      {/* Filters */}
      <div className="mb-4">
        <FiltersSection
          filters={filters}
          localSearch={localSearch}
          availableModels={availableModels}
          availableCampaigns={availableCampaigns}
          isLoading={modelsLoading}
          hasActiveFilters={hasActiveFilters}
          onSearchChange={setLocalSearch}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          onModelSearchChange={handleModelSearchChange}
          onLoadChart={handleLoadChart}
        />
      </div>

      {/* Table */}
      <TransactionsTable
        transactions={transactions}
        isLoading={isLoading}
        totalCount={totalCount}
        totalAmount={apiTotalAmount - apiTotalFee}
        totalIncome={totalIncome}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

export default RevshareTransactions;
